import React, { useEffect, useMemo } from 'react';
import { CourseLayout } from './layout/CourseLayout';
import { CourseContent, SidebarGroup } from './types';
import SpriteDefs from './SpriteDefs';
import { useParams, useLocation } from 'react-router-dom';
import { boasVindasArticle, boasVindasVideo } from './boasVindasData';
import Sidebar from './Sidebar';
import { modules, getModuleByItemId } from './registry';
import { lessonsById } from './registry';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Página genérica que replica o layout/conteúdo do "Boas-Vindas" para qualquer item.
 * Mantém o mesmo vídeo e artigo, apenas marca o item correspondente no sidebar.
 */
const GenericCourse: React.FC = () => {
  const { secaoId, itemId } = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Log para depuração
  useEffect(() => {
    console.log(`🔍 GenericCourse: Renderizando item ${itemId}`);
    console.log(`🔍 GenericCourse: URL atual: ${location.pathname}`);
    console.log(`🔍 GenericCourse: Estado de autenticação: ${isAuthenticated ? 'Autenticado' : 'Não autenticado'}`);
    
    // Verifica se há um usuário no localStorage (para depuração)
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        console.log('✅ GenericCourse: Usuário encontrado no localStorage');
      } else {
        console.log('❌ GenericCourse: Nenhum usuário encontrado no localStorage');
      }
    } catch (e) {
      console.warn('⚠️ GenericCourse: Erro ao acessar localStorage:', e);
    }
  }, [itemId, location.pathname, isAuthenticated]);

  // Determina o módulo com base no itemId via registro central
  const id = (itemId || '').trim();
  const currentModule = getModuleByItemId(id) || modules[0];

  // marca ativo no sidebar e monta breadcrumbs com Módulo > Curso > Aula
  const moduleTitle = currentModule.name;

  // Descobre curso e lesson ativos
  const activeHref = `/area/produto/item/${id}`;
  const [activeCourseTitle, activeLessonTitle] = useMemo(() => {
    let courseTitle = '';
    let lessonTitle = '';
    for (const group of currentModule.sidebar) {
      const match = group.items.find(it => (it.href || '').split('?')[0] === activeHref);
      if (match) {
        courseTitle = group.title;
        lessonTitle = match.label;
        break;
      }
    }
    return [courseTitle, lessonTitle];
  }, [currentModule, activeHref]);

  const sidebar: SidebarGroup[] = useMemo(() => currentModule.sidebar, [currentModule]);

  const content: CourseContent = useMemo(() => {
    // tenta obter lição específica por itemId (migrado para arquivos por lição)
    const lesson = lessonsById[id];
    return {
      itemId: id,
      slug: 'aula',
      secaoId: secaoId || '426683',
      title: lesson?.title || activeLessonTitle || (sidebar[0]?.items.find(i => i.active)?.label || 'Aula'),
      breadcrumbs: [
        { label: 'Início', href: '/vitrine' },
        { label: moduleTitle },
        ...(activeCourseTitle ? [{ label: activeCourseTitle }] : []),
        ...(activeLessonTitle ? [{ label: activeLessonTitle }] : []),
      ],
      video: lesson?.video || boasVindasVideo,
      article: lesson?.article || boasVindasArticle,
      sidebar,
    };
  }, [id, secaoId, sidebar, moduleTitle, activeCourseTitle, activeLessonTitle]);

  useEffect(() => {
    // Permite scroll e injeta CSS da área (mesmo que BoasVindas)
    const previousBodyClass = document.body.className;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    document.body.classList.add('area', 'aula', 'netflix', 'area-produto');

    let stylesLink = document.querySelector('link[data-vitrine-styles]') as HTMLLinkElement | null;
    if (!stylesLink) {
      stylesLink = document.createElement('link');
      stylesLink.rel = 'stylesheet';
      stylesLink.href = '/vitrine_assets/styles.css';
      stylesLink.setAttribute('data-vitrine-styles', 'true');
      document.head.appendChild(stylesLink);
    }
    // Se o stylesheet escuro existir, garante que ele venha DEPOIS do styles.css
    const ensureDarkStylesLast = () => {
      const darkEl = document.getElementById('dark-styles') as HTMLLinkElement | null;
      if (darkEl) document.head.appendChild(darkEl);
    };
    ensureDarkStylesLast();

    // Verifica se estamos em um ambiente móvel
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('📱 GenericCourse: Detectado acesso via dispositivo móvel');
      
      // Verifica se estamos acessando de um ambiente diferente
      const isLocalhost = window.location.hostname === 'localhost';
      const isProd = !isLocalhost;
      
      if (isProd) {
        console.log('🌐 GenericCourse: Acessando de ambiente de produção');
        
        // Força a verificação de autenticação para evitar problemas de sessão
        try {
          const storedUser = localStorage.getItem('user');
          if (!storedUser && isAuthenticated) {
            console.log('⚠️ GenericCourse: Usuário autenticado mas não encontrado no localStorage, salvando...');
            // Se o usuário está autenticado mas não está no localStorage, salva no localStorage
            // Isso ajuda a manter a sessão consistente entre ambientes
            const user = {
              id: 'session-recovery',
              email: 'session@recovery.temp',
              emailConfirmed: true
            };
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (e) {
          console.warn('⚠️ GenericCourse: Erro ao manipular localStorage:', e);
        }
      }
    }

    return () => {
      document.body.className = previousBodyClass;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isAuthenticated]);

  return (
    <>
      <SpriteDefs />
      <CourseLayout content={content} right={<Sidebar sidebar={sidebar} moduleTitle={moduleTitle} activeHref={`/area/produto/item/${id}`} />} />
    </>
  );
};

export default GenericCourse;

